import { ChatModel } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { Command, Popover } from '@utima/ui';
import { FormControl } from '@utima/ui-informed';
import { Check, ChevronsUpDown, Info } from 'lucide-react';
import { useState } from 'react';

import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';

import { useModelCatalog, type CatalogModel } from './useModelCatalog';

function Picker({
  value,
  onChange,
  auxiliary,
  id,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  auxiliary: boolean;
  id?: string;
  disabled?: boolean;
}) {
  const { t } = useLingui();
  const { data, locale, money, isError, refetch } = useModelCatalog();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [legacy, setLegacy] = useState(false);
  const selected = data?.models.find(model => model.id === value);
  const groups = auxiliary
    ? [
        {
          id: 'auxiliary',
          title: t`Economical background models`,
          items:
            data?.auxiliaryOrder.flatMap(modelId =>
              data.models.filter(model => model.id === modelId),
            ) ?? [],
        },
      ]
    : [
        {
          id: 'recommended',
          title: t`Recommended choices`,
          items:
            data?.models.filter(model => model.section === 'recommended') ?? [],
        },
        {
          id: 'alternatives',
          title: t`Premium and alternatives`,
          items:
            data?.models.filter(model => model.section === 'alternatives') ??
            [],
        },
        {
          id: 'experimental',
          title: t`Experimental`,
          items:
            data?.models.filter(model => model.section === 'experimental') ??
            [],
        },
        {
          id: 'legacy',
          title: t`Legacy models`,
          items: data?.models.filter(model => model.section === 'legacy') ?? [],
        },
      ];
  const price = (model: CatalogModel) =>
    model.measurement?.mean == null
      ? t`Price estimate unavailable`
      : t`~${money(model.measurement.mean)} / answer`;
  const hint = auxiliary
    ? t`Used for short background tasks such as follow-up suggestions and moderation. Main-answer prices do not apply.`
    : t`Estimated usage charges in your organization's currency, including its pricing multiplier. Based on measured questions, not a fixed fee. Context, tools and answer length affect the charge.`;

  return (
    <div className='space-y-2'>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type='button'
            id={id}
            disabled={disabled || !data}
            aria-label={auxiliary ? t`Auxiliary model` : t`Model`}
            aria-expanded={open}
            className='flex w-full items-center justify-between rounded-radius border border-border bg-white px-3 py-2 text-left disabled:opacity-60'
          >
            <span>
              <span className='block font-medium'>
                {selected?.name ?? value ?? t`Choose a model`}
              </span>
              {selected && !auxiliary && (
                <span className='block text-xs text-muted-fg'>
                  {price(selected)}
                </span>
              )}
            </span>
            <ChevronsUpDown className='size-4 shrink-0' />
          </button>
        </Popover.Trigger>
        <Popover.Content className='w-[28rem] max-w-[calc(100vw-2rem)] p-0'>
          <Command.Root>
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder={t`Search models…`}
            />
            <Command.List className='max-h-[min(24rem,calc(var(--radix-popover-content-available-height)_-_6rem))]'>
              <Command.Empty>{t`No models found`}</Command.Empty>
              {groups
                .filter(
                  group => group.id !== 'legacy' || legacy || search.length > 0,
                )
                .map(group => (
                  <Command.Group key={group.id} heading={group.title}>
                    {group.items.map(model => (
                      <Command.Item
                        key={model.id}
                        value={`${model.name} ${model.id}`}
                        onSelect={() => {
                          onChange(model.id);
                          setOpen(false);
                        }}
                        className={`flex items-start gap-2 border-l-4 py-3 ${value === model.id ? 'border-purple-600 bg-purple-50 ring-1 ring-inset ring-purple-200 data-[selected=true]:bg-purple-100' : 'border-transparent'}`}
                      >
                        <span className='min-w-0 flex-1'>
                          <span className='block font-medium'>
                            {model.name}
                            {model.id ===
                              (auxiliary
                                ? ChatModel.Gemini25FlashLite
                                : ChatModel.Gpt6Luna) && (
                              <span className='ml-2 rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-800'>{t`Recommended`}</span>
                            )}
                          </span>
                          <span className='block text-xs text-muted-fg'>
                            {auxiliary
                              ? model.auxiliarySummary[locale]
                              : model.summary[locale]}
                          </span>
                          {!auxiliary && (
                            <span className='mt-1 block text-xs'>
                              {price(model)}
                              {model.measurement?.currentConfiguration &&
                                model.measurement.completionSeconds != null &&
                                ` · ${t`~${model.measurement.completionSeconds.toFixed(0)}s to complete`}`}
                            </span>
                          )}
                        </span>
                        <Popover.Root>
                          <SimpleTooltip
                            title={
                              auxiliary
                                ? `${model.auxiliarySummary[locale]} ${hint}`
                                : model.detail[locale]
                            }
                          >
                            <Popover.Trigger asChild>
                              <button
                                type='button'
                                aria-label={model.name}
                                onClick={event => event.stopPropagation()}
                                onPointerDown={event => event.stopPropagation()}
                                onKeyDown={event => event.stopPropagation()}
                                className='rounded p-1 text-muted-fg hover:bg-purple-100 focus-visible:ring-2 focus-visible:ring-purple-500'
                              >
                                <Info className='size-4' />
                              </button>
                            </Popover.Trigger>
                          </SimpleTooltip>
                          <Popover.Content
                            side='left'
                            className='z-50 max-w-[min(20rem,calc(100vw-2rem))] p-3 text-sm'
                            onClick={event => event.stopPropagation()}
                          >
                            {auxiliary
                              ? `${model.auxiliarySummary[locale]} ${hint}`
                              : model.detail[locale]}
                          </Popover.Content>
                        </Popover.Root>
                        {value === model.id && (
                          <Check className='mt-1 size-5 shrink-0 rounded-full bg-purple-600 p-0.5 text-white' />
                        )}
                      </Command.Item>
                    ))}
                  </Command.Group>
                ))}
            </Command.List>
            {!search && !auxiliary && (
              <button
                type='button'
                className='w-full border-t border-border p-2 text-left text-xs text-muted-fg'
                onClick={() => setLegacy(!legacy)}
                aria-expanded={legacy}
              >
                {legacy ? t`Hide legacy models` : t`Show legacy models`}
              </button>
            )}
          </Command.Root>
        </Popover.Content>
      </Popover.Root>
      {isError && (
        <button
          type='button'
          onClick={() => void refetch()}
          className='text-xs text-red-700'
        >{t`Unable to load model prices. Retry`}</button>
      )}
    </div>
  );
}

export function ModelPicker({ auxiliary = false }: { auxiliary?: boolean }) {
  const { t } = useLingui();

  return (
    <FormControl
      name={auxiliary ? 'smallModel' : 'model'}
      label={auxiliary ? t`Auxiliary model` : t`Model`}
      type='text'
      keepState
      render={({ userProps, fieldApi, fieldState }) => (
        <Picker
          id={userProps.id}
          auxiliary={auxiliary}
          value={String(fieldState.value ?? '')}
          disabled={userProps.readOnly || userProps.disabled}
          onChange={value => {
            fieldApi.setTouched(true);
            fieldApi.setValue(value);
          }}
        />
      )}
    />
  );
}
